import {
    React,
    useEffect,
    useState,
    useRef,
    useCallback,
} from 'react';
import Tags from './Tags';
import { UserProfiles, Technologies, savePrefilters, getRecommendationParameters, getPrefilters, saveRecommendationParameters } from '../services';
import { Profile } from '../services/Contexture/Profile';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfilesMeta } from '../redux/slices/profilesSlice';
import { fetchTechnologiesIfNeeded, invalidate } from '../redux/slices/technologiesSlice';
import { selectAllTechnologies } from '../redux/selectors/technologiesSelectors';
import {
    selectAuthUser,
} from '../redux/selectors/authSelectors';
import {
    selectUserProfile,
} from '../redux/selectors/profileSelectors';
import { MdOutlineRecommend, MdAdd, MdRemove } from 'react-icons/md';
import '../components/Filters/Filters.scss';
import { CiFilter } from 'react-icons/ci';
import FilterChip from './Filters/FilterChip';
import { DATE_FILTER_CONDITIONS, DATE_WITH_IN, recommendationParametersFields, UI_LABELS } from '../constants/constants';
import { v4 as uuidv4 } from 'uuid';
import { CreatableTechToolsSelect } from './CreatableTechToolsSelect';
import CustomFilterPopover from './Filters/CustomFilterPopover';
import { capitalizeFirst, computeIntersectionSearchLayoutFields, generateFilterChips } from '../utils/filters/helpers';
import { buildSearchCriteria } from '../utils/filters/buildSearchCriteria';
import { applySearchCriteria } from '../utils/filters/applySearchCriteria';
import CustomTooltip from './Tooltip/CustomTooltip';

const isValidForAutoSave = (repoFilters, filtersConditions, fieldName) => {
    if (fieldName?.includes('Date')) {
        const baseFieldName = fieldName.replace(/Start|End|RelDateVal$/, '');

        const condition = filtersConditions?.[baseFieldName]?.value;

        if (condition === 'range') {
            // For range, both start and end values must be present
            const start = repoFilters[`${baseFieldName}Start`];
            const end = repoFilters[`${baseFieldName}End`];
            if (!start || !end) {
                return false;
            }
        } else if (condition === 'gte' || condition === 'lte') {
            // For gte/lte, main field value must be present
            const value = repoFilters[baseFieldName];
            if (!value) {
                return false;
            }
        } else if (condition === 'withIn') {
            // For withIn, both duration value and time unit must be present
            const duration = repoFilters[`${baseFieldName}RelDateVal`];
            const timeUnit = filtersConditions?.[`${baseFieldName}RelDate`]?.value;
            if (!duration || !timeUnit) {
                return false;
            }
        }
        return true
    }
}

const selectStyles = {
    control: (styles) => ({ ...styles, backgroundColor: 'white' }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            backgroundColor: '#fff',
            color: isDisabled ? '#ccc' : isSelected ? '#fcb12e' : '#4D5C6D',
            ':hover': {
                color: '#fcb12e',
                cursor: 'pointer',
            },
            ':focus': {
                color: '#fcb12e',
                cursor: 'pointer',
            },
            ':active': {
                color: '#fcb12e',
                cursor: 'pointer',
            },
        };
    },
    multiValue: (styles, { data }) => {
        return {
            ...styles,
            backgroundColor: 'rgb(158, 13, 61)',
            color: '#fff',
            fontWeight: 700,
        };
    },
    multiValueLabel: (styles, { data }) => ({
        ...styles,
        color: data.color,
    }),
    multiValueRemove: (styles, { data }) => ({
        ...styles,
        color: data.color,
    }),
};

const TagsWithFilters = ({ profileKey = '', profiles, name, activeGroupKey, showTools = true }) => {

    const profile = profileKey ? profiles[profileKey] :profiles[`${name}_${activeGroupKey}`];
    const dispatch = useDispatch();
    const user = useSelector(selectAuthUser);
    const userProfile = useSelector(selectUserProfile);
    const allTechnologies = useSelector(selectAllTechnologies);
    const contextureMetadata = useSelector((state) => state.contextureMetadata);
    const repoId = profile?.repositoryId;
    const intersectionSearchLayoutFields = computeIntersectionSearchLayoutFields(
        contextureMetadata?.repos,
        [repoId]
    );
    const filteredFilterFields = intersectionSearchLayoutFields?.filter((field) => field?.fieldName !== 'text');
    const [selectedValues, setSelectedValues] = useState([]);
    const [termsValues, setTermsValues] = useState([]);
    const [options, setOptions] = useState([]);
    const [showTagAdd, setShowTagAdd] = useState(false);
    const saveInProgress = useRef(false);
    const [showFieldPopover, setShowFieldPopover] = useState(false);
    const [expandedFieldKey, setExpandedFieldKey] = useState(null);
    const [activeFilters, setActiveFilters] = useState({}); // Dynamic filters state
    const [showRecommendationPopover, setShowRecommendationPopover] = useState(false);
    const [recommendationParameters, setRecommendationParameters] = useState({});
    const [isUnifiedSectionExpanded, setIsUnifiedSectionExpanded] = useState(false);
    const [filtersConditions, setFiltersConditions] = useState({ publishDate: DATE_FILTER_CONDITIONS[0], publishDateRelDate: DATE_WITH_IN[0] });
    const [storedPrefilters, setStoredPrefilters] = useState(null);
    const isApplyingStoredFilters = useRef(false);
    const saveTimeoutRef = useRef(null);

    const handleRecommendationParametersChange = (fieldName, value, reset) => {
        if (reset) {
            setRecommendationParameters({});
            return;
        }
        setRecommendationParameters((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const handleRecommendationBlur = () => {
        setTimeout(async () => {
            try {
                const profileId = profile?.profileId;
                if (!profileId) {
                    return;
                }

                await saveRecommendationParameters(recommendationParameters, { profileId });
                toast.success('Recommendation parameters saved successfully!');
                window.dispatchEvent(new Event('refreshDocLists'));
            } catch (error) {
                toast.error('Failed to save recommendation parameters');
            }
        }, 100);
    };

    const handleFiltersOnBlur = (event) => {
        const fieldName = event?.target?.name || event?.target?.getAttribute('name') || '';
        if (isValidForAutoSave(activeFilters, filtersConditions, fieldName)) {
            saveFiltersWithValidation(activeFilters);
        }
    };

    // Save filters with validation helper function
    const saveFiltersWithValidation = useCallback(async (repoFilters, filtersCondition) => {
        try {
            const profileId = profile?.profileId;
            if (!profileId) {
                return;
            }

            const searchCriteria = buildSearchCriteria({
                searchValue: '',
                repoFilters: repoFilters,
                filtersConditions: filtersCondition ? filtersCondition : filtersConditions,
            });

            await savePrefilters(searchCriteria, { profileId });
            window.dispatchEvent(new Event('refreshDocLists'));
            toast.success('Filters saved successfully!');
        } catch (error) {
            toast.error('Failed to save filters');
        }
    }, [profile?.profileId, filtersConditions]);

    // Stable handler functions that use refs
    const handleFilterChange = useCallback((fieldName, value) => {
        setActiveFilters(prev => ({
            ...prev,
            [fieldName]: value
        }));
        const isInputField = fieldName.includes('Date');
        if (!isInputField && !isApplyingStoredFilters.current) {
            const filters = { ...activeFilters, [fieldName]: value }
            saveFiltersWithValidation(filters);
        }
    }, [activeFilters, saveFiltersWithValidation]);

    // Handle filter clearing with API call for date fields
    const handleFilterClear = useCallback((fieldName, value) => {
        // First update the filter state
        handleFilterChange(fieldName, value);

        // For date fields, immediately save after clearing
        if (fieldName.includes('Date')) {
            // Clear any existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // For range fields (Start/End), use debouncing to batch multiple updates
            const isRangeField = fieldName.includes('Start') || fieldName.includes('End');
            const delay = isRangeField ? 100 : 0; // 100ms delay for range fields, immediate for single fields

            saveTimeoutRef.current = setTimeout(() => {
                const updatedFilters = { ...activeFilters, [fieldName]: value };
                saveFiltersWithValidation(updatedFilters);
                saveTimeoutRef.current = null;
            }, delay);
        }
    }, [handleFilterChange, activeFilters, saveFiltersWithValidation]);

    // Handle condition changes with immediate save for date fields
    const handleConditionChange = useCallback((fieldName, condition) => {
        setFiltersConditions(prev => ({
            ...prev,
            [fieldName]: condition
        }));

        // For date fields, immediately save when condition changes
        if (fieldName.includes('Date')) {
            // Clear any existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Create updated filters based on condition change
            let updatedFilters = { ...activeFilters };

            // Reset filter values based on condition change
            const baseFieldName = fieldName.replace(/RelDate$/, '');
            const currentCondition = filtersConditions[baseFieldName]?.value;
            const newCondition = condition?.value;

            if (currentCondition !== newCondition) {
                if (newCondition === 'range') {
                    // Reset relative date and single date values
                    updatedFilters[baseFieldName] = null;
                    updatedFilters[`${baseFieldName}RelDateVal`] = null;
                } else if (newCondition === 'gte' || newCondition === 'lte') {
                    // Reset range and relative date values
                    updatedFilters[`${baseFieldName}Start`] = null;
                    updatedFilters[`${baseFieldName}End`] = null;
                    updatedFilters[`${baseFieldName}RelDateVal`] = null;
                } else if (newCondition === 'withIn') {
                    // Reset range and single date values
                    updatedFilters[`${baseFieldName}Start`] = null;
                    updatedFilters[`${baseFieldName}End`] = null;
                    updatedFilters[baseFieldName] = null;
                }
                saveFiltersWithValidation(updatedFilters, { ...filtersConditions, [fieldName]: condition });
            } else {
                saveTimeoutRef.current = setTimeout(() => {
                    if (isValidForAutoSave(updatedFilters, { ...filtersConditions, [fieldName]: condition }, fieldName)) {
                        saveFiltersWithValidation(updatedFilters, { ...filtersConditions, [fieldName]: condition });
                        saveTimeoutRef.current = null;
                    }
                }, 100);
            }
        }
    }, [activeFilters, filtersConditions, saveFiltersWithValidation]);

    const filterChips = generateFilterChips({
        fields: filteredFilterFields,
        filters: activeFilters,
        filtersConditions: filtersConditions,
        FilterChange: handleFilterClear,
    });

    const recommendationChips = generateFilterChips({
        fields: recommendationParametersFields,
        filters: recommendationParameters,
        filtersConditions: {},
        FilterChange: handleRecommendationParametersChange,
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const profileId = profile?.profileId;
                if (profileId) {
                    setActiveFilters({})
                    setFiltersConditions({ publishDate: DATE_FILTER_CONDITIONS[0], publishDateRelDate: DATE_WITH_IN[0] })

                    // Add small delay to prevent race conditions
                    await new Promise(resolve => setTimeout(resolve, 300));

                    const prefiltersResponse = await getPrefilters({ profileId });
                    if (prefiltersResponse?.criteria?.[0]) {
                        setStoredPrefilters(prefiltersResponse?.criteria?.[0]);
                    }

                    // Add delay before next request
                    await new Promise(resolve => setTimeout(resolve, 400));

                    const recommendationParamsResponse = await getRecommendationParameters({ profileId });
                    if (recommendationParamsResponse) {
                        setRecommendationParameters(recommendationParamsResponse);
                    }
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };

        fetchInitialData();
    }, [activeGroupKey, profile?.profileId]);

    // Separate useEffect to build search criteria when both prefilters and contextureMetadata are available
    useEffect(() => {
        if (storedPrefilters && contextureMetadata?.repos && repoId) {
            isApplyingStoredFilters.current = true;
            applySearchCriteria({
                searchCriteria: storedPrefilters,
                repositoryIds: [repoId],
                contextureMetadata,
                onSearchChange: () => { },
                onRepoFilterChange: handleFilterChange,
                setFiltersConditions: setFiltersConditions,
                onSortChange: () => { }, // No sorting in TagsWithFilters
            });
            // Reset the flag after a short delay to ensure all filter changes are processed
            setTimeout(() => {
                isApplyingStoredFilters.current = false;
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedPrefilters, contextureMetadata, repoId]);

    // Initialize selected values from userProfile or profiles
    useEffect(() => {
        if (name === 'tools' && userProfile?.tools) {
            const toolsArray = userProfile.tools
                .split(',')
                .map((tool) => tool.trim())
                .filter((tool) => tool);

            const toolsOptions = toolsArray.map((tool) => ({
                value: tool,
                label: tool,
            }));
            setSelectedValues(toolsOptions);
            setTermsValues(toolsOptions);
        } else if (name === 'stack' && userProfile?.techStack) {
            const techStackArray = userProfile.techStack
                .split(',')
                .map((tech) => tech.trim())
                .filter((tech) => tech);

            const techStackOptions = techStackArray.map((tech) => ({
                value: tech,
                label: tech,
            }));
            setSelectedValues(techStackOptions);
            setTermsValues(techStackOptions);
        } else {
            // For other profiles, use custom terms or tags from profiles
            const profileKey = `${name}_${activeGroupKey}`;
            const profile = profiles[profileKey];
            const customTerms = profile?.customTerms || [];
            const profileTags = profile?.tags || [];
            const tagsToDisplay = customTerms.length > 0 ? customTerms : profileTags;

            const tagOptions = tagsToDisplay.map((tag) => ({
                value: tag,
                label: tag,
            }));
            setSelectedValues(tagOptions);
            setTermsValues(tagOptions);
        }
    }, [userProfile, name, activeGroupKey, profiles]);

    useEffect(() => {
        if (!user?.repositoryId || (name !== 'tools' && name !== 'stack')) {
            return;
        }

        dispatch(fetchTechnologiesIfNeeded(user.repositoryId));
    }, [dispatch, user?.repositoryId, name]);

    useEffect(() => {
        if (!user?.repositoryId || (name !== 'tools' && name !== 'stack')) {
            return;
        }

        const techType = name === 'tools' ? 'tool' : 'stack';
        const filteredTechnologies = allTechnologies.filter(
            (tech) => tech.type === techType
        );

        const availableOptions = filteredTechnologies.map((tech) => ({
            value: tech.name,
            label: tech.name,
            userId: tech.user_id,
            techId: tech.tech_id,
        }));

        const currentSelectedValues = selectedValues;
        const mergedOptions = [...availableOptions];

        currentSelectedValues.forEach((selected) => {
            if (!mergedOptions.find((option) => option.value === selected.value)) {
                mergedOptions.push(selected);
            }
        });

        setOptions(mergedOptions);
    }, [
        user?.repositoryId,
        name,
        selectedValues,
        allTechnologies,
    ]);

    // Save to backend and Contexture for all relevant profiles, then refresh Redux profiles state
    const saveToContexture = async (selectedOptions) => {
        if (!profiles || !user?.profile?.['cognito:username'] || !userProfile) {
            console.log('saveToContexture: Missing profiles, user, or userProfile');
            return;
        }
        const values = selectedOptions.map((option) => option.value);

        if (values?.length === 0 || !values.join(',')) {
            toast.warn(`At least one ${name === 'tools' ? 'tool' : 'technology'} must remain selected.`, {
                position: 'top-right',
                autoClose: 3000,
            });
            setSelectedValues(termsValues);
            return;
        }

        const updateType = name === 'tools' ? 'tools' : 'technologies';
        const loadingToastId = toast.loading(`Updating ${updateType}...`, {
            position: 'top-right',
        });

        try {
            const userId = user.profile['cognito:username'];
            const commaSeparatedValues = values.join(',');

            // Update UserProfile in DynamoDB
            await UserProfiles.update(userId, {
                userId: userId,
                tools: userProfile.tools || '',
                techStack: userProfile.techStack || '',
                contextureEmailAddress: user.emailAddress,
                // Add the current profile's custom terms to the update
                [`${name}CustomTerms`]: commaSeparatedValues,
            });

            // Update Contexture custom terms for all relevant profiles
            if (name === 'tools') {
                const profileKeys = ['tools_news', 'tools_cve', 'tools_podcasts'];

                for (const key of profileKeys) {
                    const profile = profiles[key];
                    if (profile?.profileId && values.length > 0) {
                        await Profile.createOrUpdateCustomTerms(
                            { terms: values },
                            { profileId: profile.profileId }
                        );
                    }
                }
            } else if (name === 'stack') {
                const profileKeys = ['stack_news', 'stack_cve', 'stack_podcasts'];

                for (const key of profileKeys) {
                    const profile = profiles[key];
                    if (profile?.profileId && values.length > 0) {
                        await Profile.createOrUpdateCustomTerms(
                            { terms: values },
                            { profileId: profile.profileId }
                        );
                    }
                }
            } else {
                // For other profiles, use the current profile only
                const profileKey = `${name}_${activeGroupKey}`;
                const profile = profiles[profileKey];

                if (profile && profile.profileId && values.length > 0) {
                    await Profile.createOrUpdateCustomTerms(
                        { terms: values },
                        { profileId: profile.profileId }
                    );
                }
            }

            // Refresh profiles metadata in Redux so userProfile/applicationProfile stay in sync
            dispatch(fetchProfilesMeta());

            // Dismiss loading toast and show success toast
            toast.dismiss(loadingToastId);
            toast.success(`${name} updated successfully!`, {
                position: 'top-right',
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Error saving to Contexture and UserProfile:', error);

            // Dismiss loading toast and show error toast
            toast.dismiss(loadingToastId);
            toast.error(`Error updating ${name}`, {
                position: 'top-right',
                autoClose: 4000,
            });
        }
    };

    const handleSelectOnChange = async (selectedOptions) => {
        setSelectedValues(selectedOptions || []);
        if (!saveInProgress.current) {
            saveInProgress.current = true;
            await saveToContexture(selectedOptions || []);
            setTimeout(() => { saveInProgress.current = false; }, 50);
        }
    }
    //keep
    const handleOnCreateNewOption = async (inputValue) => {
        const newOption = {
            value: inputValue,
            label: inputValue,
        };
        const updatedSelectedValues = [...selectedValues, newOption];
        setSelectedValues(updatedSelectedValues);
        setOptions((prev) => [...prev, newOption]);


        const techType = name === 'tools' ? 'tool' : 'stack';
        const userId = user?.profile?.['cognito:username'];

        try {
            if (userId) {
                await Technologies.create({
                    name: inputValue,
                    tech_id: uuidv4(),
                    type: techType,
                    user_id: userId,
                });

                if (user?.repositoryId) {
                    dispatch(invalidate());
                    dispatch(fetchTechnologiesIfNeeded(user.repositoryId));
                }
            }
        } catch (error) {
            toast.error('Failed to create new tag');
            return;
        }

        if (!saveInProgress.current) {
            saveInProgress.current = true;
            await saveToContexture(updatedSelectedValues);
            setTimeout(() => { saveInProgress.current = false; }, 50);
        }
    }

    //keep
    const handleOnTrashClick = async (e, opt) => {
        e.stopPropagation();
        if (!opt.techId) return;

        try {
            const response = await Technologies.delete(
                opt.techId,
                {
                    profileId: user.repositoryId,
                }
            );

            if (
                user?.repositoryId &&
                response?.success
            ) {
                dispatch(invalidate());
                dispatch(
                    fetchTechnologiesIfNeeded(
                        user.repositoryId
                    )
                );
            }

            setSelectedValues((prev) =>
                prev.filter(
                    (item) => item.value !== opt.value
                )
            );

            const deletedTypeMessage =
                name === 'tools'
                    ? 'Tool deleted successfully'
                    : 'Tech stack deleted successfully';

            toast.success(deletedTypeMessage);
        } catch (error) {
            toast.error('Failed to delete');
        }
    }

    return (
        <div className="unified-expandable-section">
            <div
                className="unified-section-header"
                onClick={() => setIsUnifiedSectionExpanded(!isUnifiedSectionExpanded)}
                style={{ cursor: 'pointer' }}
            >
                <span className="unified-section-title">Customize Parameters</span>
                <span className="unified-section-toggle">
                    {isUnifiedSectionExpanded ? <MdRemove /> : <MdAdd />}
                </span>
            </div>

            {isUnifiedSectionExpanded && (
                <div className="unified-section-content">
                    {/* Tags/Creatable Selection */}
                    {showTools && <div className="unified-filter-section">
                        <div className="unified-section-label">
                            {UI_LABELS[name] ? capitalizeFirst(UI_LABELS[name]) : 'Tags'}
                        </div>
                        {(() => {
                            if (name === 'tools' || name === 'stack') {
                                const tagsToDisplay = selectedValues.map((item) => item.value);

                                const customAddComponent = (
                                    <div className='mb-3'>
                                        <CreatableTechToolsSelect
                                            isMulti
                                            placeholder={`Select ${name === 'tools' ? 'Tools' : 'Technologies'} or create new ones`}
                                            value={selectedValues}
                                            options={options}
                                            styles={selectStyles}
                                            isLoading={false}
                                            onChange={(selectedOptions) =>
                                                handleSelectOnChange(selectedOptions)
                                            }
                                            onCreateOption={(inputValue) =>
                                                handleOnCreateNewOption(inputValue)
                                            }
                                            getUserId={() => user?.profile?.['cognito:username']}
                                            noOptionsMessage={() =>
                                                `No ${name === 'tools' ? 'Tools' : 'Technologies'
                                                } found. Type to create a new one.`
                                            }
                                            onDeleteOption={(opt) =>
                                                handleOnTrashClick({ stopPropagation: () => { } }, opt)
                                            }
                                        />
                                    </div>
                                );

                                return (
                                    <Tags
                                        group={name}
                                        tags={tagsToDisplay}
                                        activeTags={[]}
                                        className='main-filter-section'
                                        handleSelected={(tag) => {
                                            // Handle tag selection/removal
                                            const currentValues = selectedValues.map(
                                                (item) => item.value
                                            );
                                            const isSelected = currentValues.includes(tag);

                                            let newValues;
                                            if (isSelected) {
                                                // Remove tag
                                                newValues = selectedValues.filter(
                                                    (item) => item.value !== tag
                                                );
                                            } else {
                                                // Add tag
                                                newValues = [
                                                    ...selectedValues,
                                                    { value: tag, label: tag },
                                                ];
                                            }

                                            setSelectedValues(newValues);
                                            saveToContexture(newValues);
                                        }}
                                        showTagAdd={showTagAdd}
                                        setShowTagAdd={setShowTagAdd}
                                        customAddComponent={customAddComponent}
                                    />
                                );
                            }

                            // For other profiles, use the original logic with Tags component
                            const profileKey = `${name}_${activeGroupKey}`;
                            const profile = profiles[profileKey];
                            const customTerms = profile?.customTerms || [];
                            const profileTags = profile?.tags || [];
                            const tagsToDisplay =
                                customTerms.length > 0 ? customTerms : profileTags;

                            return tagsToDisplay.length > 0 ? (
                                <Tags
                                    group={name}
                                    tags={tagsToDisplay}
                                    activeTags={[]}
                                    className='main-filter-section'
                                    handleSelected={(tag) => {
                                        // For other profiles, we need to update the selectedValues and save
                                        const currentValues = selectedValues.map(
                                            (item) => item.value
                                        );
                                        const isSelected = currentValues.includes(tag);

                                        let newValues;
                                        if (isSelected) {
                                            // Remove tag
                                            newValues = selectedValues.filter(
                                                (item) => item.value !== tag
                                            );
                                        } else {
                                            // Add tag
                                            newValues = [...selectedValues, { value: tag, label: tag }];
                                        }

                                        setSelectedValues(newValues);
                                    }}
                                />
                            ) : (
                                <div className='no-tags'>No tags available for {name}</div>
                            );
                        })()}
                    </div>}

                    {/* Filter Section */}
                    <div className="unified-filter-section">
                        <div className="unified-section-label">Filters</div>
                        <div className="d-flex column-row justify-content-end">
                            <div className="filter-chips-container">
                                {filterChips.length > 0 ? (
                                    filterChips.map((chip) => (
                                        <FilterChip
                                            key={chip.key}
                                            label={chip.label}
                                            value={chip.value}
                                            onClear={chip.onClear}
                                        />
                                    ))
                                ) : (
                                    !showFieldPopover && <li className='alert-no-tags'>
                                        No filters applied yet. Click the filter icon to add filters!{' '}
                                        <span style={{ fontSize: '1.5em' }}>👉🏻</span>
                                    </li>
                                )}
                            </div>
                            <div className="position-relative">
                                <CustomTooltip text="Click to refine recommendations" show={!showFieldPopover}>
                                    <CiFilter
                                        style={{
                                            fontSize: '38px',
                                            marginTop: '14px',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            setShowRecommendationPopover(false);
                                            setShowFieldPopover((open) => !open);
                                        }}
                                    />
                                </CustomTooltip>
                                {showFieldPopover && (
                                    <CustomFilterPopover
                                        setShowFieldPopover={setShowFieldPopover}
                                        setExpandedFieldKey={setExpandedFieldKey}
                                        expandedFieldKey={expandedFieldKey}
                                        filteredFilterFields={filteredFilterFields}
                                        repoFilters={activeFilters}
                                        filtersConditions={filtersConditions}
                                        onRepoFilterChange={handleFilterChange}
                                        setFiltersConditions={handleConditionChange}
                                        onBlur={handleFiltersOnBlur}
                                    />
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Recommendation Section */}
                    <div className="unified-filter-section">
                        <div className="unified-section-label">Recommendation Parameters</div>
                        <div className="d-flex column-row justify-content-end">
                            <div className="recommendation-chips-container">
                                {recommendationChips.length > 0 ? (
                                    recommendationChips.map((chip) => (
                                        <FilterChip
                                            key={chip.key}
                                            label={chip.label}
                                            value={chip.value}
                                            onClear={chip.onClear}
                                            showClear={false}
                                        />
                                    ))

                                ) : (
                                    !showRecommendationPopover && <li className='alert-no-tags'>
                                        No recommendation parameters set. Click the icon to set recommendation parameters!{' '}
                                        <span style={{ fontSize: '1.5em' }}>👉🏻</span>
                                    </li>
                                )}
                            </div>
                            <div style={{ position: 'relative' }} className={`${(recommendationParameters?.minScore > 0.05 || recommendationParameters?.maxDocs > 1) ? 'ms-1' : ''}`}>
                                <div className="position-relative">
                                    <CustomTooltip text="Set recommendation parameters" show={!showRecommendationPopover}>
                                        <MdOutlineRecommend
                                            style={{
                                                fontSize: '32px',
                                                marginTop: '13px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                setShowFieldPopover(false);
                                                setShowRecommendationPopover((open) => !open);
                                            }}
                                        />
                                    </CustomTooltip>

                                    {showRecommendationPopover && (
                                        <CustomFilterPopover
                                            popoverLabel='Recommendation Parameters'
                                            setShowFieldPopover={setShowRecommendationPopover}
                                            setExpandedFieldKey={setExpandedFieldKey}
                                            expandedFieldKey={expandedFieldKey}
                                            filteredFilterFields={recommendationParametersFields}
                                            repoFilters={recommendationParameters}
                                            filtersConditions={{}}
                                            onRepoFilterChange={handleRecommendationParametersChange}
                                            setFiltersConditions={null}
                                            onBlur={handleRecommendationBlur}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    )
}

export default TagsWithFilters;
